// SPDX-License-Identifier: AGPL-3.0-or-later

import type React from 'react';

export const SERVER_TAG_BADGES = [
	{id: 'none', label: 'No Icon (Text Only)'},
	{id: 'leaf', label: 'Leaf'},
	{id: 'sword', label: 'Sword'},
	{id: 'heart', label: 'Heart'},
	{id: 'flame', label: 'Flame'},
	{id: 'water', label: 'Water Drop'},
	{id: 'skull', label: 'Skull'},
	{id: 'moon', label: 'Crescent Moon'},
	{id: 'lightning', label: 'Lightning'},
	{id: 'sparkle', label: 'Sparkle'},
	{id: 'mushroom', label: 'Mushroom'},
	{id: 'crown', label: 'Crown'},
	{id: 'shield', label: 'Shield'},
	{id: 'diamond', label: 'Diamond'},
	{id: 'flower', label: 'Flower'},
	{id: 'star', label: 'Star'},
	{id: 'trophy', label: 'Trophy'},
	{id: 'rocket', label: 'Rocket'},
	{id: 'ghost', label: 'Ghost'},
	{id: 'gamepad', label: 'Gamepad'},
	{id: 'bolt', label: 'Double Bolt'},
	{id: 'gem', label: 'Gem'},
	{id: 'crosshair', label: 'Crosshair'},
	{id: 'dice', label: 'Dice'},
	{id: 'music', label: 'Music'},
	{id: 'paw', label: 'Paw'},
	{id: 'coffee', label: 'Coffee'},
	{id: 'anchor', label: 'Anchor'},
	{id: 'compass', label: 'Compass'},
] as const;

export const SERVER_TAG_COLORS = [
	'#f47fff', // ND Pink
	'#5865f2', // ND Blurple
	'#00f0ff', // Electric Cyan
	'#57cc99', // Emerald Green
	'#f4a261', // Peach Orange
	'#e9c46a', // Royal Gold
	'#ff5376', // Crimson Ruby
	'#a2d2ff', // Sky Blue
	'#c77dff', // Bright Purple
	'#b7e4c7', // Mint Green
	'#ff99c8', // Rose Pink
	'#b5a6c9', // Lavender Gray
	'#f28482', // Coral Salmon
	'#ffffff', // Radiant White
] as const;

export const SERVER_TAG_GRADIENTS = [
	{id: 'cyberpunk', label: 'Cyberpunk', value: 'linear-gradient(135deg, #7048e8, #00f0ff)'},
	{id: 'sunset', label: 'Sunset Blaze', value: 'linear-gradient(135deg, #ff5e62, #ff9966)'},
	{id: 'golden', label: 'Golden Royalty', value: 'linear-gradient(135deg, #f7971e, #ffd200)'},
	{id: 'aurora', label: 'Emerald Aurora', value: 'linear-gradient(135deg, #11998e, #38ef7d)'},
	{id: 'twilight', label: 'Deep Twilight', value: 'linear-gradient(135deg, #667eea, #764ba2)'},
	{id: 'prism', label: 'Cosmic Prism', value: 'linear-gradient(135deg, #8a2387, #e94057, #f27121)'},
	{id: 'frost', label: 'Frostbite', value: 'linear-gradient(135deg, #00c6ff, #0072ff)'},
	{id: 'sakura', label: 'Sakura Blossom', value: 'linear-gradient(135deg, #ff9a9e, #fecfef)'},
] as const;

export const QUICK_EMOJI_TAG_PRESETS = [
	'🛡️', '⚔️', '👑', '🔥', '⚡', '🌟', '🎮', '💎', '🚀', '👾', '🎯', '🐉', '🌸', '🍕', '🏆', '💀',
] as const;

export function parseGradientColors(gradientStr: string): {c1: string; c2: string} {
	const matches = gradientStr.match(/#[a-fA-F0-9]{3,8}|rgba?\([^)]+\)/g);
	if (matches && matches.length >= 2) {
		return {c1: matches[0], c2: matches[matches.length - 1]};
	}
	return {c1: '#7048e8', c2: '#00f0ff'};
}

interface BadgeIconProps {
	badge?: string | null;
	size?: number;
	className?: string;
	color?: string;
}

export const ServerTagIcon: React.FC<BadgeIconProps> = ({
	badge = 'leaf',
	size = 14,
	className,
	color,
}) => {
	if (!badge || badge === 'none') {
		return null;
	}
	const isSvgBadge = SERVER_TAG_BADGES.some((b) => b.id === badge && b.id !== 'none');
	if (!isSvgBadge && badge) {
		return (
			<span
				className={className}
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					fontSize: `${size}px`,
					lineHeight: 1,
					userSelect: 'none',
				}}
			>
				{badge}
			</span>
		);
	}

	const isGradient = color ? color.startsWith('linear-gradient') : false;
	const {c1, c2} = isGradient ? parseGradientColors(color!) : {c1: '', c2: ''};
	const gradId = `tag-grad-${badge}-${c1.replace(/[^a-zA-Z0-9]/g, '')}-${c2.replace(/[^a-zA-Z0-9]/g, '')}`;
	const iconColor = isGradient ? `url(#${gradId})` : (color || 'currentColor');
	const s = size;

	const defs = isGradient ? (
		<defs>
			<linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
				<stop offset="0%" stopColor={c1} />
				<stop offset="100%" stopColor={c2} />
			</linearGradient>
		</defs>
	) : null;

	switch (badge) {
		case 'sword':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M13 1L15 3L8 10L6.5 8.5L13 1ZM3 15L1 13L2.5 11.5L4.5 13.5L3 15ZM7.5 9.5L6.5 10.5L3.5 7.5L4.5 6.5L7.5 9.5ZM5.5 10.5L2 14L4 14L5.5 12.5L5.5 10.5Z" fill={iconColor} />
				</svg>
			);
		case 'heart':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 14S1 9.5 1 5A4 4 0 0 1 8 2.5 4 4 0 0 1 15 5c0 4.5-7 9-7 9Z" fill={iconColor} />
				</svg>
			);
		case 'flame':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1C7 4 4 6 4 9A4 4 0 0 0 12 9C12 5 9 3 8 1ZM8 12A2 2 0 0 1 6 10C6 8.5 7.5 7.5 8 6C8.5 7.5 10 8.5 10 10A2 2 0 0 1 8 12Z" fill={iconColor} />
				</svg>
			);
		case 'water':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1.5C8 1.5 3 7.5 3 10.5A5 5 0 0 0 13 10.5C13 7.5 8 1.5 8 1.5Z" fill={iconColor} />
				</svg>
			);
		case 'skull':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1A6 6 0 0 0 2 7C2 10 4 11 5 12V14H11V12C12 11 14 10 14 7A6 6 0 0 0 8 1ZM5.5 8A1.5 1.5 0 1 1 5.5 5A1.5 1.5 0 0 1 5.5 8ZM10.5 8A1.5 1.5 0 1 1 10.5 5A1.5 1.5 0 0 1 10.5 8ZM7 12H9V14H7V12Z" fill={iconColor} />
				</svg>
			);
		case 'moon':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M13 9.5A6 6 0 1 1 6.5 3 6.5 6.5 0 0 0 13 9.5Z" fill={iconColor} />
				</svg>
			);
		case 'lightning':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M9.5 1L2 9H8L6.5 15L14 7H8L9.5 1Z" fill={iconColor} />
				</svg>
			);
		case 'sparkle':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1L9.5 6.5L15 8L9.5 9.5L8 15L6.5 9.5L1 8L6.5 6.5L8 1Z" fill={iconColor} />
				</svg>
			);
		case 'mushroom':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1A7 7 0 0 0 1 8C1 9 2 9.5 3 9.5H13C14 9.5 15 9 15 8A7 7 0 0 0 8 1ZM6 9.5V14C6 14.5 6.5 15 7 15H9C9.5 15 10 14.5 10 14V9.5H6ZM4.5 6A1.5 1.5 0 1 1 4.5 3A1.5 1.5 0 0 1 4.5 6ZM11.5 6A1.5 1.5 0 1 1 11.5 3A1.5 1.5 0 0 1 11.5 6Z" fill={iconColor} />
				</svg>
			);
		case 'crown':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M2 13H14V11H2V13ZM14 4L11 7L8 2L5 7L2 4L3 10H13L14 4Z" fill={iconColor} />
				</svg>
			);
		case 'shield':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1.5L2 4V8.5C2 12 5 14.5 8 15C11 14.5 14 12 14 8.5V4L8 1.5Z" fill={iconColor} />
				</svg>
			);
		case 'diamond':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M4 2L1 6L8 14L15 6L12 2H4ZM4.5 3H11.5L13.5 5.5H2.5L4.5 3ZM8 7.5L3.5 6.5H12.5L8 7.5Z" fill={iconColor} />
				</svg>
			);
		case 'flower':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 5.5A2.5 2.5 0 1 0 8 10.5A2.5 2.5 0 1 0 8 5.5ZM8 1A2 2 0 0 0 6 3A2 2 0 0 0 8 5A2 2 0 0 0 10 3A2 2 0 0 0 8 1ZM1 8A2 2 0 0 0 3 10A2 2 0 0 0 5 8A2 2 0 0 0 3 6A2 2 0 0 0 1 8ZM15 8A2 2 0 0 0 13 6A2 2 0 0 0 11 8A2 2 0 0 0 13 10A2 2 0 0 0 15 8ZM8 15A2 2 0 0 0 10 13A2 2 0 0 0 8 11A2 2 0 0 0 6 13A2 2 0 0 0 8 15Z" fill={iconColor} />
				</svg>
			);
		case 'star':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1.5L9.9 5.8L14.5 6.3L11 9.5L12 14.1L8 11.8L4 14.1L5 9.5L1.5 6.3L6.1 5.8L8 1.5Z" fill={iconColor} />
				</svg>
			);
		case 'trophy':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M3 2H13V5C13 7.8 10.8 10 8 10C5.2 10 3 7.8 3 5V2ZM1 3H3V5.5C3 6.3 3.3 7 3.8 7.6C2.2 7 1 5.5 1 3.8V3ZM15 3V3.8C15 5.5 13.8 7 12.2 7.6C12.7 7 13 6.3 13 5.5V3H15ZM7 11.9V13H5V15H11V13H9V11.9C11.3 11.4 13 9.4 13 7V1H3V7C3 9.4 4.7 11.4 7 11.9Z" fill={iconColor} />
				</svg>
			);
		case 'rocket':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1C10.5 1 13 4 13 8C13 9.5 12 11 11 12L12 15L9.5 13.5L8 15L6.5 13.5L4 15L5 12C4 11 3 9.5 3 8C3 4 5.5 1 8 1ZM8 5A1.5 1.5 0 1 0 8 8A1.5 1.5 0 0 0 8 5Z" fill={iconColor} />
				</svg>
			);
		case 'ghost':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1A6 6 0 0 0 2 7V15L4.5 13.5L7 15L8 14L9 15L11.5 13.5L14 15V7A6 6 0 0 0 8 1ZM5.5 6A1.5 1.5 0 1 1 5.5 9A1.5 1.5 0 0 1 5.5 6ZM10.5 6A1.5 1.5 0 1 1 10.5 9A1.5 1.5 0 0 1 10.5 6Z" fill={iconColor} />
				</svg>
			);
		case 'gamepad':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M5 3H11C13.5 3 15.5 5 15.5 7.5C15.5 10 13.5 13 11.5 13C10.5 13 10 12 9 12H7C6 12 5.5 13 4.5 13C2.5 13 0.5 10 0.5 7.5C0.5 5 2.5 3 5 3ZM4.5 6V7.5H3V8.5H4.5V10H5.5V8.5H7V7.5H5.5V6H4.5ZM11.5 6.5A1 1 0 1 0 11.5 8.5A1 1 0 0 0 11.5 6.5ZM13 8.5A1 1 0 1 0 13 10.5A1 1 0 0 0 13 8.5Z" fill={iconColor} />
				</svg>
			);
		case 'bolt':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M9 1L3 8.5H8L7 15L13 7.5H8L9 1Z" fill={iconColor} />
				</svg>
			);
		case 'gem':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M4 2L1 6L8 14L15 6L12 2H4ZM4.5 3.5H11.5L13 5.5H3L4.5 3.5ZM8 12L3.5 6.5H12.5L8 12Z" fill={iconColor} />
				</svg>
			);
		case 'crosshair':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1V3M8 13V15M1 8H3M13 8H15M8 4A4 4 0 1 0 8 12A4 4 0 0 0 8 4ZM8 7A1 1 0 1 1 8 9A1 1 0 0 1 8 7Z" stroke={iconColor} strokeWidth="1.75" strokeLinecap="round" fill="none" />
				</svg>
			);
		case 'dice':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M3 1H13A2 2 0 0 1 15 3V13A2 2 0 0 1 13 15H3A2 2 0 0 1 1 13V3A2 2 0 0 1 3 1ZM5.5 4A1.5 1.5 0 1 0 5.5 7A1.5 1.5 0 0 0 5.5 4ZM10.5 4A1.5 1.5 0 1 0 10.5 7A1.5 1.5 0 0 0 10.5 4ZM5.5 9A1.5 1.5 0 1 0 5.5 12A1.5 1.5 0 0 0 5.5 9ZM10.5 9A1.5 1.5 0 1 0 10.5 12A1.5 1.5 0 0 0 10.5 9Z" fill={iconColor} />
				</svg>
			);
		case 'music':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M6 3V11.5A2.5 2.5 0 1 1 3.5 9C4.1 9 4.6 9.2 5 9.6V5H13V10.5A2.5 2.5 0 1 1 10.5 8C11.1 8 11.6 8.2 12 8.6V3H6Z" fill={iconColor} />
				</svg>
			);
		case 'paw':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M4 2A1.5 1.5 0 1 1 4 5A1.5 1.5 0 0 1 4 2ZM8 1A1.5 1.5 0 1 1 8 4A1.5 1.5 0 0 1 8 1ZM12 2A1.5 1.5 0 1 1 12 5A1.5 1.5 0 0 1 12 2ZM8 6C5 6 4 8 4 10C4 12 5.5 14 8 14C10.5 14 12 12 12 10C12 8 11 6 8 6Z" fill={iconColor} />
				</svg>
			);
		case 'coffee':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M2 4H12V10C12 12.2 10.2 14 8 14H6C3.8 14 2 12.2 2 10V4ZM12 5H14C14.6 5 15 5.4 15 6C15 7.4 14.1 8.6 12.8 8.9L12 9V5ZM1 15H13V16H1V15Z" fill={iconColor} />
				</svg>
			);
		case 'anchor':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1A2 2 0 1 0 8 5A2 2 0 0 0 8 1ZM7 5V7H5V8H7V13.8C4.5 13.4 2.6 11.4 2.1 8.8L3.5 8.5L2 6L0.5 8.5L1.8 8.7C2.4 12.1 5 14.7 8 14.9C11 14.7 13.6 12.1 14.2 8.7L15.5 8.5L14 6L12.5 8.5L13.9 8.8C13.4 11.4 11.5 13.4 9 13.8V8H11V7H9V5H7Z" fill={iconColor} />
				</svg>
			);
		case 'compass':
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path d="M8 1C4.1 1 1 4.1 1 8C1 11.9 4.1 15 8 15C11.9 15 15 11.9 15 8C15 4.1 11.9 1 8 1ZM11.5 4.5L9.5 9.5L4.5 11.5L6.5 6.5L11.5 4.5ZM8 7A1 1 0 1 0 8 9A1 1 0 0 0 8 7Z" fill={iconColor} />
				</svg>
			);
		case 'leaf':
		default:
			return (
				<svg width={s} height={s} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
					{defs}
					<path
						d="M14.5 1.5C14.5 1.5 9 1 5 5C2 8 2 12 2 14C4 14 8 14 11 11C15 7 14.5 1.5 14.5 1.5ZM5 11C5.5 10.5 7 9 9 7"
						stroke={iconColor}
						strokeWidth="1.75"
						strokeLinecap="round"
						strokeLinejoin="round"
						fill={iconColor}
					/>
				</svg>
			);
	}
};
